import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Circle, Clock } from 'lucide-react';

import { JobOper, PORelInfo } from '../../services/types';
import apiService from '../../services/apiService';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useTooltipTapToggle,
} from '../ui';
import { parseCalendarDate } from '../../utils/calendarDate';

interface JobOperTableProps {
  operations: JobOper[];
}

interface SubContractBadgeProps {
  poRels: PORelInfo[];
  loading: boolean;
}

// The PO details open on tap as well as on hover: the shop floor reads this on
// touch screens, which have no hover at all.
const SubContractBadge: React.FC<SubContractBadgeProps> = ({
  poRels,
  loading,
}) => {
  const { t } = useTranslation();
  const tapToggle = useTooltipTapToggle();

  let tooltipContent: React.ReactNode;

  if (loading) {
    tooltipContent = <div className="text-gray-300 italic">Loading...</div>;
  } else if (poRels.length === 0) {
    tooltipContent = (
      <div className="text-gray-300 italic">No Active Purchase Orders</div>
    );
  } else {
    tooltipContent = poRels.map((rel, index) => (
      <React.Fragment key={`${rel.poNum}-${index}`}>
        {index > 0 && <div className="border-t border-gray-600 my-1.5" />}
        <div className="font-semibold mb-0.5">{rel.vendorName}</div>
        <div className="text-gray-300">PO: {rel.poNum}</div>
        <div className="text-gray-300">
          Due Date:{' '}
          {rel.dueDate
            ? parseCalendarDate(rel.dueDate).toLocaleDateString()
            : '—'}
        </div>
      </React.Fragment>
    ));
  }

  return (
    <Tooltip delayDuration={0} {...tapToggle.rootProps}>
      <TooltipTrigger asChild>
        <button
          type="button"
          {...tapToggle.triggerProps}
          className="cursor-pointer ml-2 inline-block px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded"
        >
          {t('jobTracker.subContract')}
        </button>
      </TooltipTrigger>
      <TooltipContent
        variant="dark"
        side="top"
        align="start"
        sideOffset={6}
        className="w-56 py-2"
      >
        <div className="text-xs">{tooltipContent}</div>
      </TooltipContent>
    </Tooltip>
  );
};

const JobOperTable: React.FC<JobOperTableProps> = ({ operations }) => {
  const { t } = useTranslation();

  // Map of "assemblySeq-oprSeq" -> PORel records for that operation
  const [poRelMap, setPoRelMap] = useState<Map<string, PORelInfo[]>>(new Map());
  const [poRelsLoading, setPoRelsLoading] = useState(false);

  useEffect(() => {
    const hasSubContracts = operations.some((op) => op.subContract);
    if (!hasSubContracts) return;

    const jobNum = operations[0]?.jobNum;
    if (!jobNum) return;

    setPoRelsLoading(true);
    apiService
      .getJobPORels(jobNum)
      .then((poRels) => {
        const map = new Map<string, PORelInfo[]>();
        for (const rel of poRels) {
          const key = `${rel.assemblySeq}-${rel.oprSeq}`;
          const existing = map.get(key) || [];
          map.set(key, [...existing, rel]);
        }
        setPoRelMap(map);
      })
      .catch(() => {
        // On error, leave map empty — tooltip will show "No Active Purchase Orders"
      })
      .finally(() => {
        setPoRelsLoading(false);
      });
  }, [operations]);

  if (operations.length === 0) {
    return (
      <div className="bg-white rounded border border-gray-300 shadow-sm">
        <div className="bg-amber-100/70 px-4 py-3 border-b border-amber-200">
          <h3 className="text-lg font-bold text-amber-900">
            {t('jobTracker.operations')} (0)
          </h3>
        </div>
        <div className="p-4 text-center text-gray-500">
          {t('jobTracker.noOperations')}
        </div>
      </div>
    );
  }

  const totalOpers = operations.length;
  const completedOpers = operations.filter((op) => op.opComplete).length;
  const totalEstHours = operations.reduce(
    (sum, op) => sum + op.estProdHours + op.estSetHours,
    0
  );
  const totalActHours = operations.reduce(
    (sum, op) => sum + op.actProdHours + op.actSetupHours,
    0
  );

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-amber-100/70 px-4 py-3 border-b border-amber-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-amber-900">
          {t('jobTracker.operations')} ({totalOpers})
        </h3>
        <p className="text-xs text-amber-800 mt-1">
          {completedOpers} {t('jobTracker.completed')} •{' '}
          {t('jobTracker.estHours')}: {totalEstHours.toFixed(2)}h •{' '}
          {t('jobTracker.actHours')}: {totalActHours.toFixed(2)}h
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto flex-grow">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 w-20">
                {t('jobTracker.oprSeq')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.opCode')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                {t('jobTracker.opDesc')}
              </th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700 w-24">
                {t('jobTracker.statusColumn')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.qtyCompleted')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-28">
                {t('jobTracker.prodQty')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-32">
                {t('jobTracker.estHours')}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700 w-32">
                {t('jobTracker.actHours')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700 w-32">
                {t('jobTracker.dueDate')}
              </th>
            </tr>
          </thead>
          <tbody>
            {operations.map((oper) => {
              const totalEstHours = oper.estProdHours + oper.estSetHours;
              const totalActHours = oper.actProdHours + oper.actSetupHours;
              const progressPct =
                oper.productionQty > 0
                  ? (oper.qtyCompleted / oper.productionQty) * 100
                  : 0;

              return (
                <tr
                  key={`${oper.jobNum}-${oper.oprSeq}`}
                  className="hover:bg-gray-50 transition-colors border-b border-gray-200"
                >
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {oper.oprSeq}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {oper.opCode}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{oper.opDesc}</div>
                    {oper.commentText && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {oper.commentText}
                      </div>
                    )}
                    {oper.subContract && (
                      <SubContractBadge
                        poRels={
                          poRelMap.get(`${oper.assemblySeq}-${oper.oprSeq}`) ||
                          []
                        }
                        loading={poRelsLoading}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {oper.opComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                    ) : progressPct > 0 ? (
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="text-xs text-blue-700">
                          {progressPct.toFixed(0)}%
                        </span>
                      </div>
                    ) : (
                      <Circle className="w-5 h-5 text-gray-400 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900">
                    {oper.qtyCompleted.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900">
                    {oper.productionQty.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {totalEstHours.toFixed(2)}h
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium">
                    {totalActHours.toFixed(2)}h
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {parseCalendarDate(oper.dueDate).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobOperTable;
